import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    getIdentityBaseUrl,
    ufoEventRedirectToProduct,
    ProductKey
} from "@/components/signup/utils";
import { useBXPExpressClient } from "@/libraries/signup/bxpkit-core/src";
import { getDataFoundationEvent } from "@/utils/data-foundations/data-foundation-types";
import { useFeatureExposed } from "@/utils/feature-exposed";
import {
    JourneyLocalStorage,
    useAnchorLocalStorage
} from "@/utils/local-storage-hook";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { useAnalyticsClient } from "../../../../../../../analytics/wac-analytics/analytics-client";
import { getIdentityTeamType } from "../models/user-segmentation";
import { generateBitbucketWorkspaceURL } from "../utils/bitbucket-confirmation";
import { createProductUrl } from "../utils/create-product-url";
import { triggerTailoredOnboardingEvent } from "../utils/trigger-tailored-onboarding-event";
import { getJtbdTemplateOverrideQueryParam } from "../utils/utils";

import { useConfirmationPage } from "./use-confirmation-page";
import { ConfirmationPageType } from "./use-confirmation-page-type";
import { addAnonIdToParams } from "./utils/with-anon-id";

import {
    useCloudStatus,
    useConfirmationPageType,
    useJiraTemplateChoice
} from ".";

export type FeatureFlags = {
    readonly provisionSecondPartyApps?: boolean;
    readonly enableBundlesAfterSignup?: boolean;
    readonly enableCutSignUpStepsInHalf?: boolean;
};

export type BlockingRedirectPromise =
    | Promise<unknown>
    | (() => Promise<unknown>);

export type CloudStatusRedirection = {
    readonly redirectUrl: string;
    readonly featureFlags: FeatureFlags;
    readonly deferRedirectView?: ConfirmationPageType;
    readonly setDeferRedirectView?: (
        deferRedirectView: ConfirmationPageType | undefined
    ) => void;
    readonly setWaitingForProvisioning: (value: boolean) => void;
    readonly setRedirectUrl: (redirectUrl: string) => void;
    readonly addBlockingRedirectPromise: (
        promise: BlockingRedirectPromise
    ) => void;
    readonly resolveBlockingRedirectPromises: () => Promise<unknown>;
    readonly createRedirectUrl: (projectKey?: string) => string | undefined;
};

export function useCloudStatusRedirection() {
    return useContext(CloudStatusRedirectionContext);
}

/* istanbul ignore next */
const CloudStatusRedirectionContext = createContext<CloudStatusRedirection>({
    redirectUrl: "",
    featureFlags: {
        provisionSecondPartyApps: false,
        enableBundlesAfterSignup: false
    },
    setWaitingForProvisioning: () => undefined,
    setRedirectUrl: () => undefined,
    addBlockingRedirectPromise: () => undefined,
    resolveBlockingRedirectPromises: async () => undefined,
    createRedirectUrl: () => undefined
});

export type CloudStatusRedirectionProviderProps = {
    readonly featureFlags: FeatureFlags;
};

export const CloudStatusRedirectionProvider =
    /* istanbul ignore next */
    ({
        children,
        featureFlags
    }: PropsWithChildren<CloudStatusRedirectionProviderProps>) => {
        const {
            createCloudStatus,
            isProduction,
            onDemandUrl,
            cloudId,
            triggerOnSuccess,
            onSuccessDuration,
            readyPollCloudStatus,
            pollCloudStatus,
            setFireOnRedirectAnalytics,
            platform,
            onSuccess
        } = useCloudStatus();
        const { createAnalyticsEvent } = useAnalyticsEvents();
        const {
            templateId,
            products,
            siteRenameRequestId,
            dataRegion,
            admin,
            isFoundingAdmin,
            signupSource,
            provisionApp,
            appKey,
            jtbd,
            jtbdPersona,
            orgId,
            edition,
            bitbucketWorkspaceUuid,
            primaryCollectionProduct
        } = useSearchParameters();
        const productKey = products[0];
        const onlyOneProductKeyExists =
            products.length === 1 ? productKey : undefined;

        const isProduct = {
            jsw: onlyOneProductKeyExists === ProductKey.JIRA_SOFTWARE,
            jpd: onlyOneProductKeyExists === ProductKey.JIRA_PRODUCT_DISCOVERY,
            jsm: onlyOneProductKeyExists === ProductKey.JIRA_SERVICE_MANAGEMENT,
            bitbucket: onlyOneProductKeyExists === ProductKey.BITBUCKET,
            twc: onlyOneProductKeyExists === ProductKey.TEAMWORK_COLLECTION,
            swc: onlyOneProductKeyExists === ProductKey.SERVICE_COLLECTION,
            wac:
                products.length > 1 &&
                (products.includes(ProductKey.JIRA_CUSTOMER_SERVICE) ||
                    products.includes(ProductKey.JIRA_PRODUCT_DISCOVERY) ||
                    products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT))
        };

        const userProfile = useLoggedInUser();
        const analyticsClient = useAnalyticsClient();

        const blockingRedirectRequests = useMemo<
            (Promise<unknown> | (() => Promise<unknown>))[]
        >(() => [], []);

        const addBlockingRedirectPromise = useCallback(
            (request: Promise<unknown> | (() => Promise<unknown>)) => {
                blockingRedirectRequests.push(request);
            },
            [blockingRedirectRequests]
        );

        const resolveBlockingRedirectPromises = useCallback(async () => {
            return Promise.all(
                blockingRedirectRequests.map((request) => {
                    // start any functions that are added to the queue
                    if (typeof request === "function") {
                        return request();
                    }

                    return request;
                })
            ).catch(() => {
                // no-op, continue even if some requests fail
            });
        }, [blockingRedirectRequests]);

        useFeatureExposed("jfind-1150-streamline-redirect-to-product", "true", {
            conditions: [productKey === ProductKey.JIRA_SOFTWARE]
        });

        useFeatureExposed("jfind-1245-confluence-streamline-redirect", "test", {
            conditions: [productKey === ProductKey.CONFLUENCE]
        });

        const {
            userSegmentationContext: {
                shouldShowUserSegmentationPage,
                selectedTeamType
            }
        } = useConfirmationPage();

        const [redirectUrl, setRedirectUrl] = useState("");

        const [deferRedirectView, setDeferRedirectView] = useState<
            ConfirmationPageType | undefined
        >(undefined);

        const currentScreen = useConfirmationPageType(
            createCloudStatus,
            deferRedirectView
        );
        const finalProvisioningScreens: ConfirmationPageType[] = useMemo(
            () => [
                "robots",
                "jira-animated-provisioning-screen",
                "redirect",
                "jira-onboarding-wizard",
                "service-collection-provisioning-screen",
                "twc-provisioning-screen"
            ],
            []
        );
        const persona = useRef("");

        const {
            templateChoice = "unknown",
            templateChoiceId,
            templateMetadata
        } = useJiraTemplateChoice();

        useEffect(() => {
            const anchorLocalStorage = getAnchorLocalStorage();

            if (anchorLocalStorage) {
                try {
                    const localStorageObj: JourneyLocalStorage =
                        JSON.parse(anchorLocalStorage);

                    if (localStorageObj.persona) {
                        persona.current = localStorageObj.persona;
                    }
                } catch (err) {
                    console.log(err);
                }
            }

            if (jtbdPersona) {
                persona.current = jtbdPersona;
            } else if (templateMetadata?.persona) {
                persona.current = templateMetadata?.persona;
            } else if (selectedTeamType) {
                if (!persona.current) {
                    persona.current = `${getIdentityTeamType(
                        selectedTeamType
                    )} Generic`;
                }
            }
        }, [templateMetadata, selectedTeamType, jtbdPersona]);

        const { getAnchorLocalStorage } = useAnchorLocalStorage();

        const localStorageObj: JourneyLocalStorage | undefined = useMemo(() => {
            const anchorLocalStorage = getAnchorLocalStorage();
            if (anchorLocalStorage) {
                try {
                    return JSON.parse(
                        anchorLocalStorage
                    ) as JourneyLocalStorage;
                } catch (err) {
                    console.log(err);
                    return undefined;
                }
            }

            return undefined;
        }, []);

        const requireWaitingForProvisioning =
            isProduct.jsw ||
            isProduct.jpd ||
            isProduct.jsm ||
            isProduct.swc ||
            isProduct.twc ||
            isProduct.wac;

        const [waitingForProvisioning, setWaitingForProvisioning] =
            useState(false);

        // Infer that the user has completed all onboarding steps
        // if the user is waiting on a provisioning screen
        const userCompletedOnboardingSteps = useMemo(() => {
            if (requireWaitingForProvisioning) {
                return waitingForProvisioning;
            } else {
                return finalProvisioningScreens.includes(currentScreen);
            }
        }, [
            requireWaitingForProvisioning,
            waitingForProvisioning,
            currentScreen
        ]);

        const createRedirectUrl = useCallback(
            (projectKey?: string) => {
                const productUrl = createProductUrl({
                    isProduction,
                    onDemandUrl,
                    products,
                    dataRegion,
                    cloudId,
                    admin,
                    createAnalyticsEvent,
                    templateChoice,
                    templateChoiceId,
                    templateId,
                    persona: persona.current,
                    jtbd,
                    siteRenameRequestId,
                    signupSource,
                    isFoundingAdmin,
                    jtbdTemplateOverride: getJtbdTemplateOverrideQueryParam(),
                    selectedTeamType,
                    orgId,
                    primaryCollectionProduct,
                    projectKey
                });

                triggerTailoredOnboardingEvent({
                    userCompletedOnboardingSteps,
                    selectedTeamType,
                    analyticsClient,
                    jtbd,
                    cloudId,
                    localStorageObj,
                    accountId: userProfile?.accountId,
                    createAnalyticsEvent,
                    persona: persona.current
                });

                createAnalyticsEvent({
                    ...getDataFoundationEvent("ProductNavigationInitialized"),
                    actionSubjectId: "newSite",
                    attributes: {
                        productUrl,
                        productKey,
                        edition
                    }
                }).fire();

                if (productKey === ProductKey.JIRA_SOFTWARE) {
                    return `${getIdentityBaseUrl()}/login?${new URLSearchParams(
                        {
                            continue: productUrl,
                            application: "jira"
                        }
                    )}`;
                }

                if (productKey === ProductKey.CONFLUENCE) {
                    return `${getIdentityBaseUrl()}/login?${new URLSearchParams(
                        {
                            continue: productUrl,
                            application: "confluence"
                        }
                    )}`;
                }

                if (isProduct.twc || isProduct.swc) {
                    return `${getIdentityBaseUrl()}/login?${new URLSearchParams(
                        {
                            continue: productUrl,
                            application: primaryCollectionProduct
                        }
                    )}`;
                }

                if (productKey === ProductKey.JIRA_PRODUCT_DISCOVERY) {
                    return `${getIdentityBaseUrl()}/login?${new URLSearchParams(
                        {
                            continue: productUrl
                        }
                    )}`;
                }

                return productUrl;
            },
            [
                isProduction,
                onDemandUrl,
                products,
                dataRegion,
                cloudId,
                admin,
                createAnalyticsEvent,
                templateChoice,
                templateChoiceId,
                templateId,
                jtbd,
                siteRenameRequestId,
                signupSource,
                isFoundingAdmin,
                selectedTeamType,
                orgId,
                userCompletedOnboardingSteps,
                analyticsClient,
                localStorageObj,
                userProfile?.accountId,
                productKey,
                edition
            ]
        );

        useEffect(() => {
            if (triggerOnSuccess) {
                runSuccess();
            }
        }, [triggerOnSuccess, onSuccessDuration]);

        useEffect(() => {
            if (readyPollCloudStatus) {
                pollCloudStatus();
            }
        }, [readyPollCloudStatus, pollCloudStatus]);

        // On provisioning finish UFO, analytics and redirection handling
        useEffect(() => {
            if (createCloudStatus !== "success") {
                return;
            }

            const isLoom = productKey === "loom";

            // verify user has made it to a final screen (i.e. robots, jira-animated-provisioning-screen, redirect).
            const isProvisionOnOrg = Boolean(orgId);
            if (!isLoom && (userCompletedOnboardingSteps || isProvisionOnOrg)) {
                if (deferRedirectView) {
                    setRedirectUrl(createRedirectUrl());
                    return;
                }
                setFireOnRedirectAnalytics(true);

                // Timeout is needed to ensure that analytics events are able to be sent before browser redirects, otherwise it will abort them.
                setTimeout(() => {
                    window.location.assign(createRedirectUrl());
                }, 100);
            }

            ufoEventRedirectToProduct.success({
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    finalProvisioningScreen: currentScreen,
                    bitbucketWorkspaceUuid
                }
            });

            if (isLoom) {
                const searchParams = new URLSearchParams(
                    window.location.search
                );

                const loomSource = searchParams.get("source");

                const loomRedirectSearchParams: Record<string, string> = {
                    ...(loomSource && { source: loomSource })
                };

                if (isProduction) {
                    loomRedirectSearchParams[
                        "redirect_after"
                    ] = `https://www.loom.com/workspace/cloud/${cloudId}`;

                    const finalParams = addAnonIdToParams(
                        loomRedirectSearchParams,
                        analyticsClient
                    );

                    window.location.assign(
                        `https://loom.com/api/auth/atlassian?${new URLSearchParams(
                            finalParams
                        ).toString()}`
                    );
                } else {
                    loomRedirectSearchParams[
                        "redirect_after"
                    ] = `https://stage.loom.com/workspace/cloud/${cloudId}`;
                    loomRedirectSearchParams["data"] =
                        searchParams.get("data") || "";

                    const finalParams = addAnonIdToParams(
                        loomRedirectSearchParams,
                        analyticsClient
                    );

                    window.location.assign(
                        `https://stage.loom.com/api/auth/atlassian?${new URLSearchParams(
                            finalParams
                        ).toString()}`
                    );
                }
            } else if (
                isProduct.bitbucket &&
                bitbucketWorkspaceUuid !== undefined
            ) {
                window.location.assign(
                    generateBitbucketWorkspaceURL(
                        isProduction,
                        bitbucketWorkspaceUuid
                    )
                );
            } else {
                setRedirectUrl(createRedirectUrl());
            }
        }, [
            currentScreen,
            createCloudStatus,
            userCompletedOnboardingSteps,
            deferRedirectView
        ]);

        const runSuccess = () => {
            onSuccess(Date.now() - onSuccessDuration);
            if (
                !shouldShowUserSegmentationPage &&
                featureFlags.provisionSecondPartyApps &&
                provisionApp &&
                platform !== "ccp" &&
                !!appKey &&
                !!onDemandUrl
            ) {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const bxpExpressClient = useBXPExpressClient();
                void bxpExpressClient.getXsrfToken().then((xsrfToken) => {
                    bxpExpressClient
                        .marketplaceAppInstall({
                            siteName: onDemandUrl,
                            appKey,
                            xsrfToken
                        })
                        .catch((err) => err);
                });
            }
        };

        return (
            <CloudStatusRedirectionContext.Provider
                value={{
                    redirectUrl,
                    createRedirectUrl,
                    featureFlags,
                    deferRedirectView,
                    setDeferRedirectView,
                    setWaitingForProvisioning,
                    setRedirectUrl,
                    addBlockingRedirectPromise,
                    resolveBlockingRedirectPromises
                }}
            >
                {children}
            </CloudStatusRedirectionContext.Provider>
        );
    };
